import _ from 'lodash';

/**
 * Indicates whether a request is pending
 *
 * @type    {Boolean}
 *
 * @private
 */
var pending = false;

/**
 * Total rows pending to add to page
 *
 * @type    {Integer}
 *
 * @private
 */
var total = 0;

/**
 * references options object, this reference is created on init method
 *
 * @type    {Object}
 *
 * @private
 */
var options = null;

/**
 * Collection of batches of templates to add to the page
 *
 * @type    {Object}
 *
 * @private
 */
var data = [];

/**
 * Pushed nodes with a sorting order currently out of range
 *
 * @type {Array}
 *
 * @private
 */
var queuedNodes = {};

/**
 * Request CB from LazyLoadingViewAdapter. This is a wrapper in order to get the current number of rows lazy
 * loaded so we can check if we can move any pushed element from the queue to the data
 *
 * @return {void}
 */
var parentRequestCB = function parentRequestCB() {
};

/**
 * Adds a new batch of data
 *
 * @param   {Object}    data    groups, sub groups and rows to add to the page
 *
 * @private
 */
var add = function add(localData) {

    data = data.concat(localData.compiled);
    total += localData.totalRows;
    pending = false;
};

/**
 * Adds a row into a specific position in the page
 *
 * @param {number} rowsOnPage total number of rows in the page
 * @param {Object} node       node object to be pushed
 * @param {number} position   position to add the node to
 *
 * @returns {void}
 *
 * @private
 */
var addInPlace = function addInPlace(rowsOnPage, node, position) {

    var total = rowsOnPage;

    /**
     * Loops through the groups
     *
     * @param  {Object} batch batch to loop through
     *
     * @return {void}
     */
    var loopOuterRows = function (batch) {

        _.each(batch.rows, loopInnerRows);
    };

    /**
     * Inserts a new node into the lazy loading data structure
     *
     * @param  {Object} batch batch to verify and insert if in range
     *
     * @return {void}
     *
     * @private
     */
    var loopInnerRows = function (row) {

        var insertIntoIndex = position - total;
        total += row.rows.length;

        // prevent the new market to be pushed if the backend has been updated in the meantime
        // this follows an optimistic approach of consistent ordering, thingers crossed
        if (total > position && row.rows[insertIntoIndex].key !== node.key) {

            row.rows.splice(insertIntoIndex, 0, node);
        }
    };

    _.each(data, loopOuterRows);
    total += 1;
};

/**
 * Checks if any of the markets in the queue is now in the page range
 *
 * @param  {number}} currentRow current row loaded on the frontend
 *
 * @return {void}
 *
 * @private
 */
var checkQueuedData = function checkQueuedData(currentRow) {

    /**
     * Checks if a node can be added to the data queue after requesting new data
     *
     * @param  {Object} item  item object to be added. contains the node template and its position
     *
     * @return {void}
     *
     * @private
     */
    var checkNode = function (item) {

        if (api.add(item, currentRow)) {

            // if added remove from queue
            delete queuedNodes[item.node.key];
        }
    };

    _.each(queuedNodes, checkNode);
};

/**
 * Get first group of data
 *
 * @returns {Object}
 */
var getFirstGroup = function getFirstGroup() {

    return data[0];
};

/**
 * Check if we have data
 *
 * @returns {boolean}
 */
var hasData = function hasData() {

    return !!getFirstGroup();
};

/**
 * Checks if we need data - if we have less than one page than we do
 *
 * @returns {boolean}
 *
 * @private
 */
var needData = function needData() {

    return total <= window.WH.sportsbook.eventsPerPage;
};

/**
 * Checks if the request is allowed
 *
 * @param   {Integer}   currentRow  current row to request
 *
 * @returns {boolean}
 */
var isNotAllowedRequest = function isNotAllowedRequest(currentRow) {

    return pendingOrFinished(currentRow) || !needData();
};

/**
 * Checks if the request is pending or if we have reached the page limit
 *
 * @param   {Integer}   currentRow  current row to request
 *
 * @returns {boolean}
 */
var pendingOrFinished = function pendingOrFinished(currentRow) {

    return pending || currentRow >= options.totalRowsCount;
};

/**
 * Checks if the compiled data is empty
 *
 * @param {Object} data Some data containing compiled templates
 *
 * @returns {boolean}
 *
 * @private
 */
var isCompiledEmpty = function isCompiledEmpty(data) {

    return _.isEmpty(data.compiled) || data.compiled[0].rows.length === 0;
};

/**
 * Checks if a given batch is empty
 *
 * @param data
 *
 * @returns {boolean}
 */
var isEmpty = function isEmpty(data) {

    return data.totalRows === 0 || isCompiledEmpty(data);
};

/**
 * Compiles a group of templates
 *
 * @param   {Integer}   maxElements     Max number of rows to include in the compiled template
 *
 * @returns {{totalItems: number, isNewGroup: boolean, isNewSubGroup: boolean, html: string}}
 */
var compileTemplate = function compileTemplate(maxElements) {

    var item = getFirstGroup();
    var batch = {
        totalItems: 0,
        isNewGroup: false,
        isNewSubGroup: false,
        html: '',
    };

    loopThroughSubGroups(item, maxElements, batch);
    compileGroupTemplate(item, batch);

    purgeData();
    return batch;
};

/**
 * Compile the group template and appends it to the batch html
 *
 * @param   {Object}  item     next data to create the batch from
 * @param   {Object}  batch    batch object
 *
 * @returns {void}
 */
var compileGroupTemplate = function compileGroupTemplate(item, batch) {

    if (item.template) {

        batch.isNewGroup = true;
        batch.isNewSubGroup = false;
        batch.html = handleNestedTemplates(
            item,
            options.replaceGroupStr,
            batch.html
        );
    }
};

/**
 * Loops through the sub groups and appends the generated html
 *
 * @param   {Object}    item            next data to create the batch from
 * @param   {Integer}   maxElements     maximum number of elements to proc
 * @param   {Object}    batch           batch object
 *
 * @returns {void}
 */
var loopThroughSubGroups = function loopThroughSubGroups(item, maxElements, batch) {

    var subGroup = item.rows[0];

    while (maxElements > 0 && subGroup) {

        maxElements -= compileRowsTemplate(subGroup, maxElements, batch);
        compileSubGroupTemplate(subGroup, batch);

        subGroup = purgeSubGroup(subGroup, item);
    }
};

/**
 * Compiles the sub group template
 *
 * @param   {Object}  subGroup  sub group containing the template
 * @param   {Object}  batch     batch object
 *
 * @returns {void}
 */
var compileSubGroupTemplate = function compileSubGroupTemplate(subGroup, batch) {

    if (subGroup.template) {

        batch.isNewSubGroup = true;
        batch.html = handleNestedTemplates(
            subGroup,
            options.replaceSubGroupStr,
            batch.html
        );
    }
};

/**
 * Compile te row templates
 *
 * @param   {Object}    subGroup        sub group containing the template
 * @param   {Integer}   maxElements     total elements to remove
 * @param   {Object}    batch           batch object
 *
 * @returns {Integer}   total removed rows
 */
var compileRowsTemplate = function compileRowsTemplate(subGroup, maxElements, batch) {

    var splicedRows = subGroup.rows.splice(0, maxElements);

    total -= splicedRows.length;

    // update batch object
    batch.totalItems += splicedRows.length;
    batch.html = _.map(splicedRows, 'template').join('');

    return splicedRows.length;
};

/**
 * Handles the replacement of a placeholder - needle - in a parent template
 * by the provided html
 *
 * @param   {Object}    item    parent item
 * @param   {String}    needle  string to be replaced
 * @param   {String}    html    replacement for needle
 *
 * @returns {String}
 */
var handleNestedTemplates = function handleNestedTemplates(item, needle, html) {

    var string = item.template.replace(
        needle,
        html
    );
    item.template = null;

    return string;
};

/**
 * Purge a position in the data array if it has no more rows
 *
 * @returns {void}
 */
var purgeData = function () {

    var batch = getFirstGroup();

    if (batch.rows.length === 0) {

        data.shift();
    }
};

/**
 * Checks if a given sub group doesn't contain any rows. Sets it to
 * null if so for GC
 *
 * @param   {Object}    subGroup    sub group to be checked
 * @param   {Object}    item        sub group parent
 *
 * @returns {Object}
 */
var purgeSubGroup = function purgeSubGroup(subGroup, item) {

    if (subGroup.rows.length === 0) {

        item.rows.shift();
        subGroup = null;
    }

    return subGroup;
};

/**
 * Stores an item in the queue to be lazy loaded as soon as it is in range
 *
 * @param {Object} item
 *
 * @returns {void}
 *
 * @private
 */
var storeInQueue = function storeInQueue(item) {

    if (!queuedNodes[item.node.key]) {

        queuedNodes[item.node.key] = item;
    }
};

/**
 * Public functions
 *
 * @type {Object}
 */
var api = {

    /**
     * Inits the repository
     *
     * @param   {Object}    localOptions
     * @param   {Function}  requestCB
     *
     * @returns {void}
     */
    init: function init(localOptions, requestCB) {

        api.destroy();
        options = localOptions;
        parentRequestCB = requestCB;
    },

    /**
     * Request Data Callback
     *
     * @param   {Object}    batch of templates to append
     */
    requestCB: function requestCB(data, currentRow) {

        if (isEmpty(data)) {

            return;
        }

        add(data);
        checkQueuedData(currentRow);
    },

    /**
     * Requests more data
     *
     * @param   {Integer}   currentRow
     */
    request: function request(currentRow) {

        currentRow += total;
        if (isNotAllowedRequest(currentRow)) {

            return;
        }

        pending = true;
        options.pagination.getMoreData(
            currentRow + 1,
            1,
            parentRequestCB
        );
    },

    /**
     * Get templates for x rows
     *
     * @param   {Integer}   maxElements
     *
     * @returns {Array}
     */
    getTemplates: function getTemplates(maxElements) {

        var nextItems = [];

        while (maxElements > 0 && hasData()) {

            var batch = compileTemplate(maxElements);

            maxElements -= batch.totalItems;
            nextItems.push(batch);
        }

        return nextItems;
    },

    /**
     * Cleans up the data
     *
     * @returns {void}
     */
    destroy: function destroy() {

        data = [];
        total = 0;
        pending = false;
    },

    /**
     * Tries to add a new node to the data model. If the node is not yet in range we queue and re-check
     * evertytime we get new data
     *
     * @param {Object} item       item to be added. {node, position}
     * @param {number} rowsOnPage total number of rows currently displayed on the frontend
     *
     * @returns {boolean}
     *
     * @public
     */
    add: function add(item, rowsOnPage) {

        // +1 to cover the immediate next position scenario
        if (rowsOnPage + total + 1 >= item.position) {

            addInPlace(rowsOnPage, item.node, item.position);
            return true;
        }

        storeInQueue(item);

        return false;
    },

    /**
     * Adds a node at a position inside lazy loading data internal storage.
     *
     * @param {Object} batch item to be added.
     * @param {Number} position where to add the batch
     * @param {Function} groupTemplateCallback page specific logic after adding a group
     * @param {Function} subgroupTemplateCallback page specific logic after adding a subgroup
     *
     * @returns {boolean}
     *
     * @public
     */
    addNodeAtPosition: function addNodeAtPosition(batch, position, groupTemplateCallback, subgroupTemplateCallback) {

        /**
         * Helper functions
         *
         * @type {Object}
         */
        var helpers = {

            /**
             * Inserts a new group inside lazy loading data internal storage.
             *
             * @returns {void}
             *
             * @private
             */
            insertGroup: function insertGroup() {

                data.splice(position - data[0].position, 0, batch);
                groupTemplateCallback(batch);
            },

            /**
             * Inserts a sub group inside lazy loading data internal storage.
             *
             * @param {Object} node Group that will receive a new subgroup
             *
             * @returns {void}
             *
             * @private
             */
            insertSubGroup: function insertSubGroup(node) {

                node.rows.splice(0, 0, batch);
                subgroupTemplateCallback(batch);
            },

            /**
             * Inserts a row inside an existing subgroup from lazy loading data internal storage.
             *
             * @param {Object} node subgroup that will receive a new row
             *
             * @returns {void}
             *
             * @private
             */
            insertInExistingSubgroup: function insertInExistingSubgroup(node) {

                //competition  -> market -> event
                node.rows = node.rows.concat(batch.rows[0].rows);
            },

            /**
             * Inserts a batch inside an existing group.
             *
             * @param {Object} node Group that will receive a new batch
             *
             * @returns {void}
             *
             * @private
             */
            insertBatchInExistingGroup: function insertBatchInExistingGroup(node) {

                var subgroup = _.find(node.rows, {
                    key: batch.rows[0].key,
                });

                if (subgroup) {

                    helpers.insertInExistingSubgroup(subgroup);
                } else {

                    helpers.insertSubGroup(node);
                }
            },
        };

        var node = _.find(data, {
            key: batch.key,
        });

        if (node) {

            helpers.insertBatchInExistingGroup(node);
        } else {

            helpers.insertGroup();
        }

        total++;
    },

    /**
     * Total rows pending to add to page
     *
     * @returns {Number}
     *
     * @public
     */
    getTotalPending: function getTotalPending() {

        return total;
    },
};

export default api;
